import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FloatingActionBar = ({ 
  hasUnsavedChanges = false, 
  onSaveDraft, 
  onSaveAndContinue, 
  onCancel,
  isLoading = false,
  lastSaved = null
}) => {
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else {
      onCancel();
    }
  };

  const confirmCancel = () => {
    setShowUnsavedWarning(false);
    onCancel();
  };

  const cancelWarning = () => {
    setShowUnsavedWarning(false);
  };

  const formatLastSaved = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const saved = new Date(timestamp);
    const diffInMinutes = Math.floor((now - saved) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Saved just now';
    if (diffInMinutes < 60) return `Saved ${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Saved ${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    return saved?.toLocaleDateString();
  };

  return (
    <>
      {/* Floating Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border shadow-legal-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Status Information */}
            <div className="flex items-center space-x-4">
              {hasUnsavedChanges && (
                <div className="flex items-center space-x-2 text-warning">
                  <Icon name="AlertCircle" size={16} />
                  <span className="text-sm font-medium">Unsaved changes</span>
                </div>
              )}
              
              {lastSaved && !hasUnsavedChanges && (
                <div className="flex items-center space-x-2 text-success">
                  <Icon name="Check" size={16} />
                  <span className="text-sm">{formatLastSaved(lastSaved)}</span>
                </div>
              )}

              {isLoading && (
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  <span className="text-sm">Saving...</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>

              <Button
                variant="outline"
                onClick={onSaveDraft}
                disabled={isLoading}
                iconName="Save"
                iconPosition="left"
              >
                Save Draft
              </Button>

              <Button
                variant="default"
                onClick={onSaveAndContinue}
                disabled={isLoading}
                loading={isLoading}
                iconName="ArrowRight"
                iconPosition="right"
              >
                Save & Continue
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={cancelWarning} />
          <div className="relative w-full max-w-md mx-4 bg-card rounded-lg shadow-legal-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Unsaved Changes</h3>
                <p className="text-sm text-muted-foreground">You have unsaved changes that will be lost.</p>
              </div>
            </div>

            <p className="text-sm text-foreground mb-6">
              Are you sure you want to leave without saving? All your changes will be lost and cannot be recovered.
            </p>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={cancelWarning}
              >
                Keep Editing
              </Button>
              <Button
                variant="destructive"
                onClick={confirmCancel}
                iconName="Trash2"
                iconPosition="left"
              >
                Discard Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingActionBar;